import type {
  ClaudeSubscriptionAuthAction,
  ClaudeSubscriptionAuthActionResult,
  ClaudeSubscriptionAuthStatus,
  CodexSubscriptionAuthAction,
  CodexSubscriptionAuthActionResult,
  CodexSubscriptionAuthStatus,
} from "@t3tools/contracts";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import { HttpClient } from "effect/unstable/http";

import type { PreparedConnection } from "./connection/model.ts";
import { ManagedRelayDpopSigner } from "./relay/managedRelay.ts";
import {
  executeEnvironmentHttpRequest,
  makeEnvironmentHttpApiClient,
  makeEnvironmentHttpApiUrlBuilder,
  type RemoteEnvironmentRequestError,
} from "./rpc/http.ts";
import {
  buildEnvironmentAuthHeaders,
  withEnvironmentCredentials,
} from "./state/environmentHttpAuth.ts";

const DEFAULT_CLAUDE_AUTH_TIMEOUT_MS = 12_000;
const DEFAULT_CODEX_AUTH_TIMEOUT_MS = 12_000;

export const fetchClaudeSubscriptionAuthStatus = Effect.fn(
  "clientRuntime.providerConnections.fetchClaudeSubscriptionAuthStatus",
)(function* (input: {
  readonly prepared: PreparedConnection;
  readonly signer: Option.Option<ManagedRelayDpopSigner["Service"]>;
  readonly timeoutMs?: number;
}) {
  const urlBuilder = makeEnvironmentHttpApiUrlBuilder(input.prepared.httpBaseUrl);
  const requestUrl = urlBuilder.providerConnections.claudeSubscriptionStatus();
  const client = yield* makeEnvironmentHttpApiClient(input.prepared.httpBaseUrl);
  const headers = yield* buildEnvironmentAuthHeaders(
    input.prepared.httpAuthorization,
    "GET",
    requestUrl,
    input.signer,
  );
  return yield* executeEnvironmentHttpRequest(
    requestUrl,
    input.timeoutMs ?? DEFAULT_CLAUDE_AUTH_TIMEOUT_MS,
    withEnvironmentCredentials(
      input.prepared.httpAuthorization,
      client.providerConnections.claudeSubscriptionStatus({ headers }),
    ),
  );
});

export const requestClaudeSubscriptionAuthAction = Effect.fn(
  "clientRuntime.providerConnections.requestClaudeSubscriptionAuthAction",
)(function* (input: {
  readonly prepared: PreparedConnection;
  readonly action: ClaudeSubscriptionAuthAction;
  readonly signer: Option.Option<ManagedRelayDpopSigner["Service"]>;
  readonly timeoutMs?: number;
}) {
  const urlBuilder = makeEnvironmentHttpApiUrlBuilder(input.prepared.httpBaseUrl);
  const requestUrl = urlBuilder.providerConnections.claudeSubscriptionAction();
  const client = yield* makeEnvironmentHttpApiClient(input.prepared.httpBaseUrl);
  const headers = yield* buildEnvironmentAuthHeaders(
    input.prepared.httpAuthorization,
    "POST",
    requestUrl,
    input.signer,
  );
  return yield* executeEnvironmentHttpRequest(
    requestUrl,
    input.timeoutMs ?? DEFAULT_CLAUDE_AUTH_TIMEOUT_MS,
    withEnvironmentCredentials(
      input.prepared.httpAuthorization,
      client.providerConnections.claudeSubscriptionAction({
        headers,
        payload: { action: input.action },
      }),
    ),
  );
});

export class ClaudeSubscriptionAuthClient extends Context.Service<
  ClaudeSubscriptionAuthClient,
  {
    readonly status: (
      prepared: PreparedConnection,
    ) => Effect.Effect<ClaudeSubscriptionAuthStatus, RemoteEnvironmentRequestError>;
    readonly action: (
      prepared: PreparedConnection,
      action: ClaudeSubscriptionAuthAction,
    ) => Effect.Effect<ClaudeSubscriptionAuthActionResult, RemoteEnvironmentRequestError>;
  }
>()("@t3tools/client-runtime/providerConnections/ClaudeSubscriptionAuthClient") {}

export const claudeSubscriptionAuthClientLayer: Layer.Layer<
  ClaudeSubscriptionAuthClient,
  never,
  HttpClient.HttpClient
> = Layer.effect(
  ClaudeSubscriptionAuthClient,
  Effect.gen(function* () {
    const httpClient = yield* HttpClient.HttpClient;
    const signer = yield* Effect.serviceOption(ManagedRelayDpopSigner);
    return ClaudeSubscriptionAuthClient.of({
      status: (prepared) =>
        fetchClaudeSubscriptionAuthStatus({ prepared, signer }).pipe(
          Effect.provideService(HttpClient.HttpClient, httpClient),
        ),
      action: (prepared, action) =>
        requestClaudeSubscriptionAuthAction({ prepared, action, signer }).pipe(
          Effect.provideService(HttpClient.HttpClient, httpClient),
        ),
    });
  }),
);

export const fetchCodexSubscriptionAuthStatus = Effect.fn(
  "clientRuntime.providerConnections.fetchCodexSubscriptionAuthStatus",
)(function* (input: {
  readonly prepared: PreparedConnection;
  readonly signer: Option.Option<ManagedRelayDpopSigner["Service"]>;
  readonly timeoutMs?: number;
}) {
  const urlBuilder = makeEnvironmentHttpApiUrlBuilder(input.prepared.httpBaseUrl);
  const requestUrl = urlBuilder.providerConnections.codexSubscriptionStatus();
  const client = yield* makeEnvironmentHttpApiClient(input.prepared.httpBaseUrl);
  const headers = yield* buildEnvironmentAuthHeaders(
    input.prepared.httpAuthorization,
    "GET",
    requestUrl,
    input.signer,
  );
  return yield* executeEnvironmentHttpRequest(
    requestUrl,
    input.timeoutMs ?? DEFAULT_CODEX_AUTH_TIMEOUT_MS,
    withEnvironmentCredentials(
      input.prepared.httpAuthorization,
      client.providerConnections.codexSubscriptionStatus({ headers }),
    ),
  );
});

export const requestCodexSubscriptionAuthAction = Effect.fn(
  "clientRuntime.providerConnections.requestCodexSubscriptionAuthAction",
)(function* (input: {
  readonly prepared: PreparedConnection;
  readonly action: CodexSubscriptionAuthAction;
  readonly signer: Option.Option<ManagedRelayDpopSigner["Service"]>;
  readonly timeoutMs?: number;
}) {
  const urlBuilder = makeEnvironmentHttpApiUrlBuilder(input.prepared.httpBaseUrl);
  const requestUrl = urlBuilder.providerConnections.codexSubscriptionAction();
  const client = yield* makeEnvironmentHttpApiClient(input.prepared.httpBaseUrl);
  const headers = yield* buildEnvironmentAuthHeaders(
    input.prepared.httpAuthorization,
    "POST",
    requestUrl,
    input.signer,
  );
  return yield* executeEnvironmentHttpRequest(
    requestUrl,
    input.timeoutMs ?? DEFAULT_CODEX_AUTH_TIMEOUT_MS,
    withEnvironmentCredentials(
      input.prepared.httpAuthorization,
      client.providerConnections.codexSubscriptionAction({
        headers,
        payload: { action: input.action },
      }),
    ),
  );
});

export class CodexSubscriptionAuthClient extends Context.Service<
  CodexSubscriptionAuthClient,
  {
    readonly status: (
      prepared: PreparedConnection,
    ) => Effect.Effect<CodexSubscriptionAuthStatus, RemoteEnvironmentRequestError>;
    readonly action: (
      prepared: PreparedConnection,
      action: CodexSubscriptionAuthAction,
    ) => Effect.Effect<CodexSubscriptionAuthActionResult, RemoteEnvironmentRequestError>;
  }
>()("@t3tools/client-runtime/providerConnections/CodexSubscriptionAuthClient") {}

export const codexSubscriptionAuthClientLayer: Layer.Layer<
  CodexSubscriptionAuthClient,
  never,
  HttpClient.HttpClient
> = Layer.effect(
  CodexSubscriptionAuthClient,
  Effect.gen(function* () {
    const httpClient = yield* HttpClient.HttpClient;
    const signer = yield* Effect.serviceOption(ManagedRelayDpopSigner);
    return CodexSubscriptionAuthClient.of({
      status: (prepared) =>
        fetchCodexSubscriptionAuthStatus({ prepared, signer }).pipe(
          Effect.provideService(HttpClient.HttpClient, httpClient),
        ),
      action: (prepared, action) =>
        requestCodexSubscriptionAuthAction({ prepared, action, signer }).pipe(
          Effect.provideService(HttpClient.HttpClient, httpClient),
        ),
    });
  }),
);
